import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, CheckCircle2, Clock, FileText, Shield, Award, Play,
  ArrowRight, AlertCircle, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const MyCompliancePage = () => {
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [policyAssignments, setPolicyAssignments] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [ackPolicy, setAckPolicy] = useState(null); // { policy, acknowledgement }
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [submittingAck, setSubmittingAck] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [progRes, policyRes] = await Promise.all([
        api.get('/training/my-progress'),
        api.get('/grc/policies/my-assignments'),
      ]);
      setTraining(progRes.data);
      setPolicyAssignments(policyRes.data);
    } catch (err) {
      toast.error('Failed to load compliance assignments');
    } finally {
      setLoading(false);
    }
  };

  const openAck = async (policyId) => {
    try {
      const res = await api.get(`/grc/policies/${policyId}/content-for-ack`);
      setAckPolicy(res.data);
      setAckDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load policy');
    }
  };

  const submitAck = async () => {
    if (!ackPolicy) return;
    setSubmittingAck(true);
    try {
      await api.post(`/grc/policies/${ackPolicy.policy.id}/acknowledge`);
      toast.success('Policy acknowledged');
      setAckDialogOpen(false);
      setAckPolicy(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setSubmittingAck(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-slate-200 rounded-xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const trainingPending = (training?.total_modules || 0) - (training?.completed || 0);
  const policyPending = policyAssignments.stats?.pending || 0;
  const totalPending = trainingPending + policyPending;

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="my-compliance-page">
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-heading tracking-tight">My Compliance</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Your assigned training modules and policies awaiting acknowledgement
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" data-testid="stat-training-pending">{trainingPending}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Training pending</p>
              </div>
              <BookOpen className="w-8 h-8 text-indigo-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" data-testid="stat-training-done">{training?.completed || 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Training completed</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" data-testid="stat-policy-pending">{policyPending}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Policies to acknowledge</p>
              </div>
              <FileText className="w-8 h-8 text-amber-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" data-testid="stat-badges">{training?.badges?.length || 0}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Badges earned</p>
              </div>
              <Award className="w-8 h-8 text-purple-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {totalPending === 0 && (
        <Card className="mb-8 bg-emerald-50 border-emerald-200" data-testid="all-clear-banner">
          <CardContent className="py-6 flex items-center gap-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-900">You&apos;re all caught up!</p>
              <p className="text-sm text-emerald-700">No training modules or policies are pending your attention.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="training" className="w-full">
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger value="training" data-testid="tab-training">
            <BookOpen className="w-4 h-4 mr-1.5" />
            Training ({trainingPending})
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">
            <FileText className="w-4 h-4 mr-1.5" />
            Policies ({policyPending})
          </TabsTrigger>
        </TabsList>

        {/* ============ Training tab ============ */}
        <TabsContent value="training" className="mt-6 space-y-4">
          {training && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Overall training progress</span>
                  <span className="text-sm text-muted-foreground">{training.completion_pct}%</span>
                </div>
                <Progress value={training.completion_pct} className="h-2" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(training.assignments || []).map((a) => (
                  <Card
                    key={a.id || a.module_code}
                    className="hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/grc/training/${a.module_code}`)}
                    data-testid={`assignment-${a.module_code}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-semibold text-sm group-hover:text-indigo-600 transition-colors">
                          {a.module_title || a.module_code}
                        </p>
                        {a.status === 'completed' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            {a.quiz_score}%
                          </Badge>
                        ) : a.status === 'in_progress' ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-1">
                            <Play className="w-3 h-3" />
                            {a.progress_pct}%
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 gap-1">
                            <Clock className="w-3 h-3" />
                            Assigned
                          </Badge>
                        )}
                      </div>
                      {a.due_date && (
                        <p className="text-xs text-muted-foreground">
                          Due: {new Date(a.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {(!training.assignments || training.assignments.length === 0) && (
                  <div className="col-span-2 text-center py-8 text-muted-foreground">
                    No training assigned yet. Browse the{' '}
                    <button
                      onClick={() => navigate('/grc/training')}
                      className="text-indigo-600 hover:underline font-medium"
                    >
                      training catalog
                    </button>
                    .
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* ============ Policies tab ============ */}
        <TabsContent value="policies" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {policyAssignments.items.map((p) => (
              <Card
                key={p.id}
                className="hover:shadow-md transition-shadow"
                data-testid={`policy-ack-${p.policy_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base font-semibold leading-tight">
                      {p.policy_title}
                    </CardTitle>
                    {p.status === 'acknowledged' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1 shrink-0">
                        <CheckCircle2 className="w-3 h-3" />
                        Acknowledged
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1 shrink-0">
                        <AlertCircle className="w-3 h-3" />
                        Pending
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-1">
                  {p.policy_description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                      {p.policy_description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {p.policy_framework && (
                      <Badge variant="outline" className="text-[10px] h-5">
                        {p.policy_framework}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] h-5">
                      v{p.policy_version}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span>Assigned {new Date(p.assigned_at).toLocaleDateString()}</span>
                    {p.acknowledged_at && (
                      <span>Ack {new Date(p.acknowledged_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  {p.status !== 'acknowledged' && (
                    <Button
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => openAck(p.policy_id)}
                      data-testid={`ack-btn-${p.policy_id}`}
                    >
                      Review & Acknowledge
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
            {policyAssignments.items.length === 0 && (
              <div className="col-span-2 text-center py-12 text-muted-foreground" data-testid="no-policies-empty">
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No policies assigned to you at this time.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ============ Ack Dialog ============ */}
      <Dialog open={ackDialogOpen} onOpenChange={setAckDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ack-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">{ackPolicy?.policy?.title}</DialogTitle>
            <DialogDescription>
              Read the policy carefully. Clicking &quot;I Acknowledge&quot; records your name, IP address,
              and a timestamp for compliance auditing.
            </DialogDescription>
          </DialogHeader>
          {ackPolicy && (
            <>
              <div className="flex items-center gap-2 flex-wrap py-2 border-y">
                {ackPolicy.policy.framework && (
                  <Badge variant="outline">{ackPolicy.policy.framework}</Badge>
                )}
                <Badge variant="outline">v{ackPolicy.policy.version || 1}</Badge>
                {ackPolicy.policy.owner && (
                  <span className="text-xs text-muted-foreground ml-2">
                    Owner: {ackPolicy.policy.owner}
                  </span>
                )}
              </div>
              <div
                className="prose prose-sm max-w-none py-4 policy-content
                           prose-headings:font-semibold prose-headings:tracking-tight
                           prose-h1:text-2xl prose-h2:text-lg prose-h3:text-base
                           prose-h1:mt-4 prose-h1:mb-3 prose-h2:mt-4 prose-h2:mb-2
                           prose-p:leading-relaxed prose-p:my-2
                           prose-li:my-0.5 prose-strong:font-semibold"
                data-testid="ack-policy-content"
                dangerouslySetInnerHTML={{
                  __html: ackPolicy.policy.content || '<p><em>No policy content available.</em></p>',
                }}
              />
              <div className="flex justify-between items-center pt-4 border-t">
                <Button variant="outline" onClick={() => setAckDialogOpen(false)}>
                  Close
                </Button>
                {ackPolicy.acknowledgement?.status !== 'acknowledged' && (
                  <Button
                    onClick={submitAck}
                    disabled={submittingAck}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="confirm-ack-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                    {submittingAck ? 'Recording...' : 'I Acknowledge'}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyCompliancePage;
